/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-named-as-default-member */

import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import ExcelJS, { Fill, Font, Row, Style } from 'exceljs'

import { workbook as workbookFormParticipants } from './formParticipants'
import {
  workbook as workbookParticipants,
  summaryParticipants,
  summaryShirts,
} from './participants'
import {
  Event as EventApi,
  Category,
  FormEvent as FormEventApi,
  FormParticipant as FormParticipantApi,
  Participant,
} from '../../../../API'
import { formatDate } from '../../../../constants/formats'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Manila')
dayjs.extend(isSameOrBefore)

export type Event = Omit<Exclude<EventApi, null>, '__typename'>
export type FormEvent = Omit<Exclude<FormEventApi, null>, '__typename'>
export type FormParticipant = Omit<
  Exclude<FormParticipantApi, null>,
  '__typename'
> & {
  tshirtCountTotal: number
}

export type ItemCount = {
  name: string
  count: number
  priority: number
}

export interface SpreadsheetInput {
  [key: string]: Participant[] | FormParticipant[]
}

export const convertToSheetData = (
  eventId: string,
  values: Participant[] | FormParticipant[],
) => {
  if (!values) return null

  const data = values.reduce((prev, cur) => {
    const key = 'categoryId' in cur ? cur?.categoryId : eventId
    return {
      ...prev,
      [key]: key in prev ? prev[key].concat(cur) : [cur],
    }
  }, {})

  return data
}

export const addRow = ({
  sheet,
  data,
  style,
  exclude,
}: {
  sheet: ExcelJS.Worksheet
  data: any[] | any
  style?: Partial<Style>[] | Partial<Style>
  exclude?: number[]
}) => {
  const row = sheet.addRow(data) as Row
  row.eachCell((cell: ExcelJS.Cell, colNum: number) => {
    if (style && !Array.isArray(style)) {
      if (exclude.includes(colNum)) return
      if ('font' in style) cell.font = style.font
      if ('fill' in style) cell.fill = style.fill
    } else {
      if ('font' in style) cell[colNum].font = style.font
      if ('fill' in style) cell[colNum].fill = style.fill
    }
  })
  return sheet
}

export const prioritySort = (a: Partial<Category>, b: Partial<Category>) => {
  if (!!a.priority && !b.priority) return -1
  if (!!b.priority && !a.priority) return 1
  return a.priority - b.priority
}

export const dateSort = (
  a: Participant | FormParticipant,
  b: Participant | FormParticipant,
) => {
  if (a.createdAt < b.createdAt) return -1
  if (a.createdAt > b.createdAt) return 1
  return 0
}

export const cleanName = (name: string) => {
  return name
    .replaceAll('*', ' ')
    .replaceAll('?', ' ')
    .replaceAll(':', ' ')
    .replaceAll('\\', ' ')
    .replaceAll('/', ' ')
    .replaceAll('[', ' ')
    .replaceAll(']', ' ')
}

export const headerStyle = {
  font: { bold: true },
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFFF09' },
  } as Fill,
}

export const dataStyle = {
  font: { bold: false, color: { argb: 'FF000000' } } as Partial<Font>,
}

export const summaryStyle = {
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD1E5C9' },
  } as Fill,
}

export const summaryHeaderStyle = {
  font: { bold: true, size: 14 },
  ...summaryStyle,
}

export const summaryFooterStyle = {
  font: { bold: true },
  ...summaryStyle,
}

export const generateSummary = ({
  event,
  data,
  type = 'participant',
}: {
  event: Event | FormEvent
  data: SpreadsheetInput
  type: 'participant' | 'shirt'
}) => {
  if (type === 'participant') return summaryParticipants(event as Event, data)
  return summaryShirts(event as Event, data)
}

export const generateSpreadSheet = async ({
  event,
  data,
  isFP = false,
  formShirtData,
}: {
  event: Event | FormEvent
  data: SpreadsheetInput
  isFP: boolean
  formShirtData: any
}) => {
  const workbook = !isFP
    ? workbookParticipants(event as Event, data)
    : workbookFormParticipants(event as FormEvent, data, formShirtData)
  const buf = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `MEGATechPH - ${event.name} - as of ${formatDate(
    new Date(),
    'YYYYMMDD.HHmm',
  )}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
